import { useContext } from "react"
import { GlobalStateContext } from "../components/providers/GlobalStateProvider.jsx"
import { useAuthenticatedFetch } from "./useAuthenticatedFetch.js"

export const useGetSubscriptions = () => {
	const authenticatedFetch = useAuthenticatedFetch()

	return async (userId, store, email = null) => {
		const params = new URLSearchParams({ userId, store })
		if (email) params.set("email", email)
		return await authenticatedFetch(
			`/api/paddle/getSubscriptions?${params}`,
		)
			.then(async (res) => {
				const json = await res.json()
				if (!res.ok || json?.subscriptionLookupFailed) {
					throw new Error(json?.error || "Subscription lookup failed")
				}
				return json
			})
			.then((json) => {
				return {
					paddleCustomerId: json?.paddleCustomerId || null,
					hasValidKopyBasicSubscription:
						json?.hasValidKopyBasicSubscription || false,
					kopyBasicSubscription: json?.kopyBasicSubscription || null,
					hasTrialedKopyBasicBefore:
						json?.hasTrialedKopyBasicBefore || null,
					otherKopyBasicSubscriptions:
						json?.otherKopyBasicSubscriptions || [],
					hasValidKopyPlusSubscription:
						json?.hasValidKopyPlusSubscription || false,
					kopyPlusSubscription: json?.kopyPlusSubscription || null,
					hasTrialedKopyPlusBefore:
						json?.hasTrialedKopyPlusBefore || null,
					hasValidKopyProtectSubscription:
						json?.hasValidKopyProtectSubscription || false,
					kopyProtectSubscription:
						json?.kopyProtectSubscription || null,
					hasTrialedKopyProtectBefore:
						json?.hasTrialedKopyProtectBefore || null,
					otherKopyProtectSubscriptions:
						json?.otherKopyProtectSubscriptions || [],
					pricing: json?.pricing || {},
					discounts: json?.discounts || {},
					hasTrialedFromEmailOfferBefore:
						json?.hasTrialedFromEmailOfferBefore || false,
					hasAnyStoreSubscriptions:
						json?.hasAnyStoreSubscriptions || false,
					hasAnyUnlinkedStoreSubscriptions:
						json?.hasAnyUnlinkedStoreSubscriptions || false,
					subscriptionLookupFailed: false,
				}
			})
			.catch((err) => {
				console.error(err)
				return {
					paddleCustomerId: null,
					hasValidKopyBasicSubscription: false,
					kopyBasicSubscription: null,
					hasTrialedKopyBasicBefore: null,
					otherKopyBasicSubscriptions: [],
					hasValidKopyPlusSubscription: false,
					kopyPlusSubscription: null,
					hasTrialedKopyPlusBefore: null,
					hasValidKopyProtectSubscription: false,
					kopyProtectSubscription: null,
					hasTrialedKopyProtectBefore: null,
					otherKopyProtectSubscriptions: [],
					pricing: {},
					discounts: {},
					hasTrialedFromEmailOfferBefore: false,
					hasAnyStoreSubscriptions: false,
					hasAnyUnlinkedStoreSubscriptions: false,
					subscriptionLookupFailed: true,
				}
			})
	}
}

export const useUncancelSubscription = () => {
	const authenticatedFetch = useAuthenticatedFetch()

	return async (subscriptionId) => {
		return await authenticatedFetch("/api/paddle/uncancelSubscription", {
			method: "POST",
			body: JSON.stringify({ subscriptionId }),
		}).catch((err) => {
			console.error(err)
		})
	}
}

export const useGetInvoices = () => {
	const { state } = useContext(GlobalStateContext)
	const authenticatedFetch = useAuthenticatedFetch()

	return async (paddleCustomerId, cursorUrl) => {
		return await authenticatedFetch("/api/paddle/getInvoices", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				paddleCustomerId,
				cursorUrl,
				store: state.shop,
			}),
		})
			.then((res) => res.json())
			.then((json) => {
				return {
					invoices: json?.invoices || [],
					nextCursorUrl: json?.nextCursorUrl || null,
				}
			})
			.catch((err) => {
				console.error(err)
				return {
					invoices: [],
					nextCursorUrl: null,
				}
			})
	}
}

export const useUpdateSubscription = () => {
	const authenticatedFetch = useAuthenticatedFetch()

	return async ({
		subscriptionId,
		items,
		prorationBillingMode,
		discountId = null,
	}) => {
		return await authenticatedFetch("/api/paddle/updateSubscription", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				subscriptionId,
				items,
				prorationBillingMode,
				discountId,
			}),
		})
			.then((res) => res.json())
			.then((json) => {
				return {
					success: json?.success || false,
					errorCode: json?.errorCode || null,
				}
			})
			.catch((err) => {
				console.error(err)
				return {
					success: false,
					errorCode: "catched_error",
				}
			})
	}
}

export const useGetPriceWithTax = () => {
	return async ({ paddleCustomerId, priceId }) => {
		return await fetch(
			`/api/paddle/getPriceWithTax?paddleCustomerId=${paddleCustomerId}&priceId=${priceId}`,
		)
			.then((res) => res.json())
			.then((json) => {
				return {
					priceWithTax: json?.priceWithTax || null,
				}
			})
			.catch((err) => {
				console.error(err)
				return {
					priceWithTax: null,
				}
			})
	}
}

export const useAddItemToSubscription = () => {
	const { state } = useContext(GlobalStateContext)
	const updateSubscription = useUpdateSubscription()

	return async ({ priceId, item, discountId = null }) => {
		const activeSubscription =
			state.kopyBasicSubscription || state.kopyProtectSubscription || null
		const activeSubscriptionId = activeSubscription?.subscriptionId || null

		const newSubscriptionItems = []

		const currentSubscriptions = [
			state.kopyBasicSubscription,
			state.kopyPlusSubscription,
			state.kopyProtectSubscription,
		]
			.filter((sub) => sub !== null)
			.filter((sub) => {
				if (sub?.priceId !== null) {
					return true
				} else {
					return false
				}
			})
		currentSubscriptions.map((sub) => {
			if (sub?.priceId) {
				newSubscriptionItems.push(sub.priceId)
			}
		})

		if (item === "basic" && !state.hasValidKopyBasicSubscription) {
			newSubscriptionItems.push(priceId)
		}

		if (item === "plus" && !state.hasValidKopyPlusSubscription) {
			newSubscriptionItems.push(priceId)
		}

		if (item === "protect" && !state.hasValidKopyProtectSubscription) {
			newSubscriptionItems.push(priceId)
		}

		if (
			newSubscriptionItems.length &&
			newSubscriptionItems.length > currentSubscriptions.length
		) {
			const { success, errorCode } = await updateSubscription({
				subscriptionId: activeSubscriptionId,
				prorationBillingMode: "prorated_immediately",
				items: newSubscriptionItems.map((itemPriceId) => {
					return {
						price_id: itemPriceId,
						quantity: 1,
					}
				}),
				discountId,
			})

			return { success, errorCode }
		} else {
			return { success: false, errorCode: "no_new_subscription_items" }
		}
	}
}

export const useRemoveItemFromSubscription = () => {
	const { state } = useContext(GlobalStateContext)
	const updateSubscription = useUpdateSubscription()

	return async (itemsToRemove) => {
		const activeSubscription =
			state.kopyBasicSubscription || state.kopyProtectSubscription || null
		const activeSubscriptionId = activeSubscription?.subscriptionId || null

		const newSubscriptionItems = []

		if (
			!itemsToRemove.includes("basic") &&
			state.hasValidKopyBasicSubscription &&
			state.kopyBasicSubscription?.priceId
		) {
			newSubscriptionItems.push(state.kopyBasicSubscription.priceId)
		}

		if (
			!itemsToRemove.includes("plus") &&
			state.hasValidKopyPlusSubscription &&
			state.kopyPlusSubscription?.priceId
		) {
			newSubscriptionItems.push(state.kopyPlusSubscription.priceId)
		}

		if (
			!itemsToRemove.includes("protect") &&
			state.hasValidKopyProtectSubscription &&
			state.kopyProtectSubscription?.priceId
		) {
			newSubscriptionItems.push(state.kopyProtectSubscription.priceId)
		}

		if (newSubscriptionItems.length) {
			const { success } = await updateSubscription({
				subscriptionId: activeSubscriptionId,
				prorationBillingMode: "full_immediately",
				items: newSubscriptionItems.map((item) => {
					return {
						price_id: item,
					}
				}),
			})

			return { success }
		} else {
			return { success: false }
		}
	}
}

export const useGetProratedPriceWithTax = () => {
	return async ({
		subscriptionId,
		newSubscriptionItem,
		discountId = null,
	}) => {
		return await fetch("/api/paddle/getProratedPriceWithTax", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				subscriptionId,
				newSubscriptionItem,
				discountId,
			}),
		})
			.then((res) => res.json())
			.then((json) => {
				return {
					proratedPriceWithTax: json?.proratedPriceWithTax || null,
				}
			})
			.catch((err) => {
				console.error(err)
				return {
					proratedPriceWithTax: null,
				}
			})
	}
}

export const useUpdateSubscriptionMetadata = () => {
	return async ({ subscriptionId, metadata }) => {
		return await fetch("/api/paddle/updateSubscriptionMetadata", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ subscriptionId, metadata }),
		})
			.then((res) => res.json())
			.then((json) => {
				return {
					success: json?.success || false,
				}
			})
			.catch((err) => {
				console.error(err)
				return {
					success: false,
				}
			})
	}
}

export const useGetPaddleCustomerAuthToken = () => {
	const { state, dispatch } = useContext(GlobalStateContext)
	const authenticatedFetch = useAuthenticatedFetch()

	return async ({ paddleCustomerId, store = state.shop }) => {
		if (!paddleCustomerId) {
			dispatch({ customerAuthToken: null })
			return { customerAuthToken: null }
		}

		const params = new URLSearchParams({ paddleCustomerId })
		if (store) params.set("store", store)

		return await authenticatedFetch(
			`/api/paddle/getPaddleCustomerAuthToken?${params}`,
		)
			.then((res) => res.json())
			.then((json) => {
				dispatch({
					customerAuthToken: json?.customerAuthToken || null,
				})

				return {
					customerAuthToken: json?.customerAuthToken || null,
				}
			})
			.catch((err) => {
				console.error(err)
				return {
					customerAuthToken: null,
				}
			})
	}
}

export const useLinkSubscriptionToStore = () => {
	const authenticatedFetch = useAuthenticatedFetch()

	return async ({ subscriptionId, store }) => {
		return await authenticatedFetch("/api/paddle/linkSubscriptionToStore", {
			method: "POST",
			body: JSON.stringify({ subscriptionId, store }),
		})
			.then((res) => res.json())
			.then((json) => ({
				success: json?.success || false,
				error: json?.error || null,
			}))
			.catch((err) => {
				console.error(err)
				return { success: false, error: "link_failed" }
			})
	}
}
